import {CwCaseTypeDto} from '../model/dto/entity/cw-case-type.dto';
import {CwCaseTypeTemplateDto} from '../model/dto/entity/cw-case-type-template.dto';
import {
    CwAddressRoutingAxis,
    CwContactRoutingGroup,
    caseTypeAddressTypeId,
    caseTypeContactRouting,
    caseTypeTemplateAddressTypeId,
    caseTypeTemplateContactRouting,
} from './cw-case-type-routing.util';

/**
 * [CW-PERSON-REFACTOR][WP-D3] The READING of the routing fallback chain — what a configurator sees
 * on a {@code cw.CwCaseType} whose routing is not (fully) configured on the case type itself.
 *
 * <h3>Why a separate file, and why it decides nothing</h3>
 * The chain itself lives in {@code cw-case-type-routing.util.ts} and mirrors the backend's
 * {@code CwPersonRoutingResolver}. This file does not re-implement it — it CALLS it and turns the
 * answer into rows a panel can render. That separation is the point: a second implementation of the
 * pair rule would drift, and it would drift in the one direction nobody notices, namely a screen that
 * claims a stage is effective when the resolver skips it.
 *
 * <h3>Five rows for eight attributes, and that is the correct shape</h3>
 * The three contact axes are PAIRS. A pair routes a value only when BOTH halves are set on the SAME
 * stage (rule G-2) — six live {@code cd.ContactType} rows carry the internal type „e-mail", so the
 * type alone does not identify a value and the field is what does. Rendering the six contact
 * attributes as six independent rows would state six independent inheritance answers where there are
 * only three, and a half-configured stage would read as „half inherited" rather than as „skipped".
 * The two address attributes have no second half and therefore ARE independent rows.
 *
 * <h3>A half-configured stage is reported, not hidden</h3>
 * The resolver skips such a stage silently (it logs, the UI does not). {@link CwRoutingInheritanceRow}
 * carries {@link CwRoutingInheritanceRow.warnings} so the panel can say so where the configurator is
 * looking, because „ich habe den Kontakttyp doch gesetzt" is exactly the state this produces.
 */

/** Which stage of the chain answered. {@code NONE} means nothing is configured anywhere. */
export type CwRoutingInheritanceSource = 'CASE_TYPE' | 'CASE_TYPE_TEMPLATE' | 'NONE';

/** The five routing axes, as identifiers. Their German labels are on the row. */
export type CwRoutingInheritanceKey =
    'CUSTOMER_ADDRESS' | 'PERSON_ADDRESS' | 'PHONE' | 'MOBILE' | 'EMAIL';

/** One rendered routing axis: what is effective, where it comes from, and what is half-done. */
export interface CwRoutingInheritanceRow {

    /** The axis. */
    readonly key: CwRoutingInheritanceKey;

    /** German label of the axis, e.g. {@code 'E-Mail'}. */
    readonly label: string;

    /** Which stage answered. */
    readonly source: CwRoutingInheritanceSource;

    /**
     * German one-liner naming the stage — {@code 'am Vorgangstyp gesetzt'},
     * {@code 'geerbt von „Standard-Routing"'} or {@code 'nicht konfiguriert'}.
     */
    readonly sourceText: string;

    /**
     * The effective value in German, both halves for a pair
     * ({@code 'E-Mail privat · Adresse'}), or an empty string when nothing is configured.
     */
    readonly valueText: string;

    /** True exactly when {@link source} is {@code CASE_TYPE_TEMPLATE} — the badge condition. */
    readonly inherited: boolean;

    /**
     * German warnings about stages that are configured by HALVES and are therefore skipped.
     * Empty in the ordinary case.
     */
    readonly warnings: readonly string[];
}

/** German labels of the five axes. */
const AXIS_LABELS: { readonly [key in CwRoutingInheritanceKey]: string } = {
    CUSTOMER_ADDRESS: 'Adressart Kunde',
    PERSON_ADDRESS: 'Adressart Person',
    PHONE: 'Telefon',
    MOBILE: 'Mobil',
    EMAIL: 'E-Mail',
};

/** The value rendered where a stage answered with nothing at all. */
const NOT_CONFIGURED = 'nicht konfiguriert';

/**
 * The five rows of one case type, in a fixed order — addresses first, then the three contact pairs.
 *
 * @param caseType the {@code cw.CwCaseType}; {@code null} while it has not been read
 * @param template its {@code cw.CwCaseTypeTemplate}; {@code null} when it references none, or while
 *   that read is still outstanding — in which case every row that WOULD inherit reports
 *   {@code NONE} rather than guessing, and the panel is expected not to render until both are in
 */
export function buildCwRoutingInheritanceRows(
    caseType: CwCaseTypeDto | null | undefined,
    template: CwCaseTypeTemplateDto | null | undefined): CwRoutingInheritanceRow[] {

    return [
        addressRow('CUSTOMER_ADDRESS', 'CUSTOMER', caseType, template),
        addressRow('PERSON_ADDRESS', 'PERSON', caseType, template),
        contactRow('PHONE', 'PHONE', caseType, template),
        contactRow('MOBILE', 'MOBILE', caseType, template),
        contactRow('EMAIL', 'EMAIL', caseType, template),
    ];
}

/** The German name of the template, quoted for a sentence; falls back to a neutral wording. */
function templateName(template: CwCaseTypeTemplateDto | null | undefined): string {
    const name = template?.name?.trim();
    return name ? `„${name}"` : 'dem Routing-Profil';
}

/**
 * One ADDRESS axis. Falls back per FIELD, because there is no second half that could be missing —
 * the asymmetry to {@link contactRow} is deliberate and mirrors the resolver.
 */
function addressRow(key: CwRoutingInheritanceKey,
                    axis: CwAddressRoutingAxis,
                    caseType: CwCaseTypeDto | null | undefined,
                    template: CwCaseTypeTemplateDto | null | undefined): CwRoutingInheritanceRow {

    const ownValue = caseTypeAddressTypeId(caseType, axis);
    if (ownValue != null) {
        return row(key, 'CASE_TYPE', 'am Vorgangstyp gesetzt',
            addressName(caseType, axis) ?? String(ownValue), []);
    }

    const templateValue = caseTypeTemplateAddressTypeId(template, axis);
    if (templateValue != null) {
        return row(key, 'CASE_TYPE_TEMPLATE', `geerbt von ${templateName(template)}`,
            templateAddressName(template, axis) ?? String(templateValue), []);
    }

    return row(key, 'NONE', NOT_CONFIGURED, '', []);
}

/**
 * One CONTACT axis — a PAIR, resolved whole or not at all.
 *
 * <p>The DECISION is delegated to {@code caseTypeContactRouting} / {@code caseTypeTemplateContactRouting},
 * which are the same functions the runtime surfaces use; only the DISPLAY of the two halves is built
 * here. A half-configured stage produces a warning rather than a value, and the resolution moves on to
 * the next stage — which is what the resolver does, and what a configurator otherwise cannot see.
 */
function contactRow(key: CwRoutingInheritanceKey,
                    group: CwContactRoutingGroup,
                    caseType: CwCaseTypeDto | null | undefined,
                    template: CwCaseTypeTemplateDto | null | undefined): CwRoutingInheritanceRow {

    const warnings: string[] = [];
    if (isHalfConfigured(contactTypeId(caseType, group), contactFieldId(caseType, group))) {
        warnings.push('Am Vorgangstyp ist nur eine Hälfte des Paares gesetzt (Kontakttyp oder Feld). '
            + 'Diese Stufe wird übersprungen.');
    }
    if (isHalfConfigured(templateContactTypeId(template, group), templateContactFieldId(template, group))) {
        warnings.push(`In ${templateName(template)} ist nur eine Hälfte des Paares gesetzt. `
            + 'Auch diese Stufe wird übersprungen.');
    }

    if (caseTypeContactRouting(caseType, group)) {
        return row(key, 'CASE_TYPE', 'am Vorgangstyp gesetzt',
            pairText(contactTypeName(caseType, group), contactFieldName(caseType, group)), warnings);
    }

    if (caseTypeTemplateContactRouting(template, group)) {
        return row(key, 'CASE_TYPE_TEMPLATE', `geerbt von ${templateName(template)}`,
            pairText(templateContactTypeName(template, group), templateContactFieldName(template, group)),
            warnings);
    }

    return row(key, 'NONE', NOT_CONFIGURED, '', warnings);
}

/** A stage that carries exactly one of the two halves — configured, and yet without effect. */
function isHalfConfigured(typeId: number | null | undefined,
                          fieldId: number | null | undefined): boolean {
    return (typeId == null) !== (fieldId == null);
}

/** {@code 'Kontakttyp · Feld'}; either half may be missing from the PROJECTION without being unset. */
function pairText(typeName: string | undefined, fieldName: string | undefined): string {
    return [typeName, fieldName].filter(part => !!part && part.trim().length).join(' · ');
}

function row(key: CwRoutingInheritanceKey,
             source: CwRoutingInheritanceSource,
             sourceText: string,
             valueText: string,
             warnings: string[]): CwRoutingInheritanceRow {
    return {
        key,
        label: AXIS_LABELS[key],
        source,
        sourceText,
        valueText,
        inherited: source === 'CASE_TYPE_TEMPLATE',
        warnings,
    };
}

// ================================================================================================
// Named accessors — one per stage and axis, so no call site spells a property out
// ================================================================================================

function addressName(caseType: CwCaseTypeDto | null | undefined,
                     axis: CwAddressRoutingAxis): string | undefined {
    return axis === 'CUSTOMER'
        ? caseType?.defaultCustomerAddressTypeDtoName
        : caseType?.defaultPersonAddressTypeDtoName;
}

function templateAddressName(template: CwCaseTypeTemplateDto | null | undefined,
                             axis: CwAddressRoutingAxis): string | undefined {
    return axis === 'CUSTOMER'
        ? template?.defaultCustomerAddressTypeDtoName
        : template?.defaultPersonAddressTypeDtoName;
}

function contactTypeId(caseType: CwCaseTypeDto | null | undefined,
                       group: CwContactRoutingGroup): number | undefined {
    return group === 'EMAIL' ? caseType?.contactTypeEmailDtoId
        : group === 'MOBILE' ? caseType?.contactTypeMobileDtoId
            : caseType?.contactTypePhoneDtoId;
}

function contactFieldId(caseType: CwCaseTypeDto | null | undefined,
                        group: CwContactRoutingGroup): number | undefined {
    return group === 'EMAIL' ? caseType?.contactTypeEmailFieldDtoId
        : group === 'MOBILE' ? caseType?.contactTypeMobileFieldDtoId
            : caseType?.contactTypePhoneFieldDtoId;
}

function contactTypeName(caseType: CwCaseTypeDto | null | undefined,
                         group: CwContactRoutingGroup): string | undefined {
    return group === 'EMAIL' ? caseType?.contactTypeEmailDtoName
        : group === 'MOBILE' ? caseType?.contactTypeMobileDtoName
            : caseType?.contactTypePhoneDtoName;
}

function contactFieldName(caseType: CwCaseTypeDto | null | undefined,
                          group: CwContactRoutingGroup): string | undefined {
    return group === 'EMAIL' ? caseType?.contactTypeEmailFieldDtoName
        : group === 'MOBILE' ? caseType?.contactTypeMobileFieldDtoName
            : caseType?.contactTypePhoneFieldDtoName;
}

function templateContactTypeId(template: CwCaseTypeTemplateDto | null | undefined,
                               group: CwContactRoutingGroup): number | undefined {
    return group === 'EMAIL' ? template?.contactTypeEmailDtoId
        : group === 'MOBILE' ? template?.contactTypeMobileDtoId
            : template?.contactTypePhoneDtoId;
}

function templateContactFieldId(template: CwCaseTypeTemplateDto | null | undefined,
                                group: CwContactRoutingGroup): number | undefined {
    return group === 'EMAIL' ? template?.contactTypeEmailFieldDtoId
        : group === 'MOBILE' ? template?.contactTypeMobileFieldDtoId
            : template?.contactTypePhoneFieldDtoId;
}

function templateContactTypeName(template: CwCaseTypeTemplateDto | null | undefined,
                                 group: CwContactRoutingGroup): string | undefined {
    return group === 'EMAIL' ? template?.contactTypeEmailDtoName
        : group === 'MOBILE' ? template?.contactTypeMobileDtoName
            : template?.contactTypePhoneDtoName;
}

function templateContactFieldName(template: CwCaseTypeTemplateDto | null | undefined,
                                  group: CwContactRoutingGroup): string | undefined {
    return group === 'EMAIL' ? template?.contactTypeEmailFieldDtoName
        : group === 'MOBILE' ? template?.contactTypeMobileFieldDtoName
            : template?.contactTypePhoneFieldDtoName;
}
